import type { PropTypes } from './Banner.types';
import type { CampaignStatus } from '../CampaignTag/CampaignTag.types';
import type { CSSProperties, ReactElement } from 'react';

import { Box, Flex, Heading, Skeleton, Text, WhaleProvider } from '@rango-dev/whale';
import { Calendar, Clock } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { calculateCampaignStatus } from '../CampaignTag/CampaignTag.helpers';
import { Timer } from '../Timer';

import { getMonthName } from './Banner.helpers';
import styles from './Banner.module.css';

const BADGE_ICON_SIZE = 16;

export function Banner(props: PropTypes): ReactElement {
  const { image, backgroundImage, className, title, description, detailsData, detailsLoading } =
    props;

  const { t } = useTranslation();
  const start = new Date(detailsData?.start || '');
  const end = new Date(detailsData?.end || '');
  let status: CampaignStatus = 'ended';

  if (detailsData) {
    status = calculateCampaignStatus(detailsData?.start, detailsData?.end);
  }
  const startMonthName = getMonthName(start);
  const endMonthName = getMonthName(end);

  if (detailsLoading || !detailsData) {
    return (
      <Skeleton
        className={styles.skeleton}
        height={{ initial: '331px', sm: '287px', md: '194px' }}
      />
    );
  }

  const containerStyle: CSSProperties | undefined = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
      }
    : undefined;

  return (
    <WhaleProvider appearance="dark">
      <Flex
        position="relative"
        width="100%"
        p={{ initial: '4', md: '5' }}
        className={`${styles.container} ${className ?? ''}`}
        style={containerStyle}>
        <Box className={styles.overlay} aria-hidden />

        <Flex
          position="relative"
          direction={{ initial: 'column', md: 'row' }}
          align="stretch"
          gap={{ initial: '4', md: '5' }}
          width={{ initial: '100%', md: 'auto' }}
          className={styles.content}>
          <Flex
            direction="column"
            align="start"
            gap="3"
            p={{ sm: '5' }}
            width={{ initial: '100%', md: 'auto' }}
            className={styles.card}>
            <Flex
              display="inline-flex"
              width="fit-content"
              align="center"
              justify="center"
              gap="1"
              px="2"
              py="1"
              className={styles.badge}>
              <Calendar size={BADGE_ICON_SIZE} className={styles.badgeIcon} aria-hidden />
              <Text as="span" size="1" weight="medium" className={styles.badgeText}>
                {`${startMonthName} ${start.getUTCDate()}`} -{' '}
                {`${endMonthName} ${end.getUTCDate()}`}
              </Text>
            </Flex>
            <Heading as="h1" size="6" weight="bold" className={styles.title}>
              {title} Campaign
            </Heading>
            <Text as="p" size="2" weight="medium" color="gray">
              {description}
            </Text>
          </Flex>

          <Flex
            direction="column"
            align="start"
            justify="center"
            gap="3"
            px={{ sm: '5' }}
            pt={{ initial: '5', sm: '4' }}
            pb={{ sm: '4' }}
            width={{ initial: '100%', md: 'auto' }}
            className={`${styles.card} ${styles.timerCard}`}>
            <Flex
              display="inline-flex"
              width="fit-content"
              align="center"
              justify="center"
              gap="1"
              px="2"
              py="1"
              className={styles.badge}>
              <Clock size={BADGE_ICON_SIZE} className={styles.badgeIcon} aria-hidden />
              <Text as="span" size="1" weight="medium" className={styles.badgeText}>
                {status === 'upcoming' && t('Coming Soon')}
                {status === 'ongoing' && t('Time Remaining')}
                {status === 'ended' && t('Campaign Ended')}
              </Text>
            </Flex>
            <Flex direction="column" gap="3">
              <Heading as="h2" size="4" weight="bold" className={styles.stateText}>
                {status === 'upcoming' && t('Get ready! Competition starting soon! 🚀')}
                {status === 'ongoing' && t('Hurry! Secure your spot now! 🚀⏳')}
                {status === 'ended' && t('Competition ended! Winners soon! 🏆')}
              </Heading>
              <Timer end={detailsData.end} start={detailsData.start} />
            </Flex>
          </Flex>
        </Flex>

        {image && <img src={image} alt={title} className={styles.bannerImage} />}
      </Flex>
    </WhaleProvider>
  );
}
